import { useState } from "react";
import { MapPin, Loader2, X, Check, Edit3 } from "lucide-react";
import { getCurrentLocation } from "@/lib/location";
import LocationPickerMap from "@/components/LocationPickerMap";

/**
 * LocationCapture — opt-in geo fix UI for entry forms.
 *
 * Four states:
 *   1. Empty — "Tag this entry with your current location" + "Pick on map"
 *   2. Capturing — spinner + "Getting fix…"
 *   3. Captured — shows place name + lat/lng + accuracy + Adjust/Remove
 *   4. Picker open — modal Leaflet map for manual correction (handles Starlink
 *      / laptop-IP fixes that come back at the wrong location)
 *
 * Props:
 *   value: { lat, lng, place_name, accuracy? } | null
 *   onChange: (next) => void
 *   compact: boolean — shrinks the empty-state copy for inline use
 */
export default function LocationCapture({ value, onChange, compact = false }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);

    const capture = async () => {
        setErr("");
        setBusy(true);
        try {
            const fix = await getCurrentLocation();
            onChange({
                lat: fix.lat,
                lng: fix.lng,
                place_name: fix.place_name,
                accuracy: fix.accuracy,
            });
        } catch (e) {
            setErr(e.message || "Couldn't get your location. Try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    const clear = () => {
        setErr("");
        onChange(null);
    };

    // Accuracy in meters — when >5km we know it's almost certainly an
    // IP-based fallback (Starlink ground station / laptop on coffee-shop WiFi).
    // Captains immediately get it when we warn them.
    const accuracy = value?.accuracy;
    const isLowAccuracy = typeof accuracy === "number" && accuracy > 5000;

    if (value && value.lat != null && value.lng != null) {
        return (
            <>
                <div
                    data-testid="location-capture-captured"
                    style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: isLowAccuracy ? "1px solid #e76f51" : "1px solid var(--bm-gold-dim)",
                        background: isLowAccuracy ? "rgba(231, 111, 81, 0.08)" : "rgba(200, 162, 92, 0.08)",
                        flexWrap: "wrap",
                    }}
                >
                    <MapPin size={14} style={{ color: isLowAccuracy ? "#e76f51" : "var(--bm-gold)", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {value.place_name && (
                            <div data-testid="location-place-name" style={{
                                fontSize: 13, color: "var(--bm-text)",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {value.place_name}
                            </div>
                        )}
                        <div data-testid="location-coords" style={{ fontSize: 11, color: "var(--bm-text-dim)" }}>
                            {value.lat.toFixed(4)}°, {value.lng.toFixed(4)}°
                            {typeof accuracy === "number" && (
                                <span> · ±{accuracy >= 1000 ? `${(accuracy / 1000).toFixed(1)}km` : `${Math.round(accuracy)}m`}</span>
                            )}
                        </div>
                        {isLowAccuracy && (
                            <div data-testid="location-low-accuracy-warning" style={{
                                fontSize: 11, color: "#e76f51", marginTop: 3,
                            }}>
                                Looks like an IP-based fix (Starlink / laptop WiFi). Tap <strong>Adjust</strong> to drop a pin where you really are.
                            </div>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        <button
                            type="button"
                            data-testid="location-adjust-btn"
                            onClick={() => setPickerOpen(true)}
                            aria-label="Adjust location on map"
                            className="bm-btn bm-btn-ghost"
                            style={{ padding: "4px 8px", fontSize: 11 }}
                        >
                            <Edit3 size={12} /> Adjust
                        </button>
                        <button
                            type="button"
                            data-testid="location-clear-btn"
                            onClick={clear}
                            aria-label="Remove location"
                            className="bm-btn bm-btn-ghost"
                            style={{ padding: "4px 8px", fontSize: 11 }}
                        >
                            <X size={12} /> Remove
                        </button>
                    </div>
                </div>
                {pickerOpen && (
                    <LocationPickerMap
                        initial={value}
                        onCancel={() => setPickerOpen(false)}
                        onConfirm={(next) => {
                            // Manually-picked fixes get accuracy reset — we know
                            // it's exactly where the captain placed the pin.
                            onChange({ ...next, accuracy: undefined });
                            setPickerOpen(false);
                        }}
                    />
                )}
            </>
        );
    }

    return (
        <div data-testid="location-capture-empty">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button
                    type="button"
                    data-testid="location-capture-btn"
                    onClick={capture}
                    disabled={busy}
                    className="bm-btn bm-btn-ghost"
                    style={{ fontSize: 13, padding: "10px 14px", opacity: busy ? 0.7 : 1 }}
                >
                    {busy ? <Loader2 size={13} className="bm-spin" /> : <MapPin size={13} />}
                    {busy ? "Getting fix…" : (compact ? "Tag location" : "Add my current location")}
                </button>
                <button
                    type="button"
                    data-testid="location-pick-map-btn"
                    onClick={() => setPickerOpen(true)}
                    className="bm-btn bm-btn-ghost"
                    style={{ fontSize: 13, padding: "10px 14px" }}
                >
                    <Edit3 size={13} /> Pick on map
                </button>
            </div>
            {!compact && !busy && !err && (
                <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--bm-text-dim)" }}>
                    Foreground only — used only when you tap this button. Never tracked in the background.
                </p>
            )}
            {err && (
                <p data-testid="location-capture-error" style={{ margin: "6px 0 0", fontSize: 12, color: "#e76f51" }}>
                    {err}
                    <button
                        type="button"
                        onClick={capture}
                        className="bm-btn bm-btn-ghost"
                        style={{ marginLeft: 8, padding: "2px 8px", fontSize: 11 }}
                    >
                        <Check size={11} /> Retry
                    </button>
                </p>
            )}
            {pickerOpen && (
                <LocationPickerMap
                    initial={{ lat: 48.42, lng: -123.36 }}  // Default centre: Salish Sea — middling-useful for first-pin
                    onCancel={() => setPickerOpen(false)}
                    onConfirm={(next) => {
                        onChange({ ...next, accuracy: undefined });
                        setPickerOpen(false);
                    }}
                />
            )}
        </div>
    );
}
